import React, {
  useState,
  useCallback,
  useImperativeHandle,
  useRef,
  ForwardedRef,
  forwardRef,
} from 'react';

import classNames from 'classnames';
import { Modal } from 'src/common/components';
import { Toast } from 'src/common/components';
import { Icon } from 'src/common/components/Icon';
import { ModalRefProps } from 'src/common/components/Modal';
import { OrbitIcons } from 'src/common/constants.';

import { updateUser, InterstellarUserRow } from '../api';
import { USER_ROLE, USER_MODAL_INPUT_CLASS, getApiErrorMessage } from '../constants';

/* ───────────────────────── types ───────────────────────── */

interface EditUserModalProps {
  accountId: number;
  onUserUpdated: () => void;
  isSharedAccount?: boolean;
}

export interface EditUserModalRefProps {
  show: () => void;
  hide: () => void;
  showWithUser: (user: InterstellarUserRow) => void;
}

interface FormState {
  cosmos_user_id: string;
  firstName: string;
  lastName: string;
  is_admin: boolean;
  phone: string;
  in_call_center: boolean;
}

interface OriginalUserState {
  is_admin: boolean;
  in_call_center: boolean;
}

const INITIAL_FORM_STATE: FormState = {
  cosmos_user_id: '',
  firstName: '',
  lastName: '',
  is_admin: false,
  phone: '',
  in_call_center: true,
};

/* ───────────────────────── helpers ───────────────────────── */

/** Check if a role_id represents an admin (Super admin or Admin) */
const isAdminRole = (roleId: number | null | undefined): boolean =>
  roleId === USER_ROLE.SUPER_ADMIN || roleId === USER_ROLE.ADMIN;

/** Validate cosmos_user_id is a positive integer */
const isValidCosmosUserId = (value: string): boolean => {
  const num = Number(value);
  return Number.isInteger(num) && num > 0;
};

/* ───────────────────────── main component ─────────────────── */

export const EditUserModal = forwardRef(
  (
    { accountId, onUserUpdated, isSharedAccount = false }: EditUserModalProps,
    ref: ForwardedRef<EditUserModalRefProps>
  ) => {
    const [formData, setFormData] = useState<FormState>({ ...INITIAL_FORM_STATE });
    const [originalState, setOriginalState] = useState<OriginalUserState>({
      is_admin: false,
      in_call_center: true,
    });
    const [loading, setLoading] = useState(false);
    const [userId, setUserId] = useState<number | string | null>(null);
    const modalRef = useRef<ModalRefProps>(null);

    useImperativeHandle(ref, () => ({
      show: () => modalRef.current?.show(),
      hide: () => modalRef.current?.hide(),
      showWithUser: (user: InterstellarUserRow) => {
        const isAdmin = isAdminRole(user.role_id);
        const inCallCenter = user.in_call_center ?? true;
        setUserId(user.id ?? null);
        setOriginalState({ is_admin: isAdmin, in_call_center: inCallCenter });
        setFormData({
          cosmos_user_id: user.cosmos_user_id != null ? String(user.cosmos_user_id) : '',
          firstName: user.firstName ?? user.first_name ?? '',
          lastName: user.lastName ?? user.last_name ?? '',
          is_admin: isAdmin,
          phone: user.phone != null ? String(user.phone) : '',
          in_call_center: inCallCenter,
        });
        modalRef.current?.show();
      },
    }));

    const handleFieldChange = useCallback((key: keyof FormState, value: string | boolean) => {
      setFormData((prev) => ({ ...prev, [key]: value }));
    }, []);

    const isFormValid = isValidCosmosUserId(formData.cosmos_user_id);

    const handleUpdate = async (): Promise<void> => {
      if (!isFormValid || userId == null) return;
      setLoading(true);
      try {
        const payload: Record<string, unknown> = {
          cosmos_user_id: Number(formData.cosmos_user_id),
        };

        // Only include name fields if they have a value (endpoint may not support clearing)
        const trimmedFirst = formData.firstName.trim();
        const trimmedLast = formData.lastName.trim();
        if (trimmedFirst) payload.firstName = trimmedFirst;
        if (trimmedLast) payload.lastName = trimmedLast;

        // Only include is_admin if it changed, to avoid accidental privilege changes
        if (formData.is_admin !== originalState.is_admin) {
          payload.is_admin = formData.is_admin;
        }

        // Only include in_call_center if it changed (shared accounts only)
        if (isSharedAccount && formData.in_call_center !== originalState.in_call_center) {
          payload.in_call_center = formData.in_call_center;
        }

        await updateUser(userId, accountId, payload);
        Toast.success('Usuario actualizado exitosamente');
        setFormData({ ...INITIAL_FORM_STATE });
        setOriginalState({ is_admin: false, in_call_center: true });
        setUserId(null);
        onUserUpdated();
        modalRef.current?.hide();
      } catch (err: unknown) {
        Toast.error(
          getApiErrorMessage(
            err,
            'Error al actualizar el usuario. Verifica los datos e intenta nuevamente.'
          )
        );
      } finally {
        setLoading(false);
      }
    };

    return (
      <Modal
        ref={modalRef}
        showFooter={false}
        actionFunc={() => void handleUpdate()}
        actionText="Guardar Cambios"
        cancelText="Cancelar"
      >
        <div className="max-h-[70vh] overflow-y-auto pr-2">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-2.0/10">
              <Icon name={OrbitIcons.Pencil} size={20} className="text-primary-2.0" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-black-1.0">Editar Usuario</h3>
              <p className="text-xs text-gray-2.0">
                Modifica los datos del usuario en Interstellar
              </p>
            </div>
          </div>

          <div className="gap-y-3 grid grid-cols-1">
            {/* Cosmos User ID - required */}
            <div>
              <label
                htmlFor="edit-user-cosmos-id"
                className="mb-1 block text-xs font-medium text-gray-3.0"
              >
                Cosmos User ID<span className="ml-0.5 text-red-500">*</span>
              </label>
              <input
                id="edit-user-cosmos-id"
                type="number"
                placeholder="Ej: 12345"
                value={formData.cosmos_user_id}
                onChange={(e) => handleFieldChange('cosmos_user_id', e.target.value)}
                className={USER_MODAL_INPUT_CLASS}
              />
              {formData.cosmos_user_id && !isFormValid && (
                <p className="mt-1 text-[10px] text-red-500">Debe ser un número entero positivo</p>
              )}
            </div>

            {/* First Name */}
            <div>
              <label
                htmlFor="edit-user-first-name"
                className="mb-1 block text-xs font-medium text-gray-3.0"
              >
                Nombre
              </label>
              <input
                id="edit-user-first-name"
                type="text"
                placeholder="Ej: Juan"
                value={formData.firstName}
                onChange={(e) => handleFieldChange('firstName', e.target.value)}
                className={USER_MODAL_INPUT_CLASS}
              />
            </div>

            {/* Last Name */}
            <div>
              <label
                htmlFor="edit-user-last-name"
                className="mb-1 block text-xs font-medium text-gray-3.0"
              >
                Apellido
              </label>
              <input
                id="edit-user-last-name"
                type="text"
                placeholder="Ej: Perez"
                value={formData.lastName}
                onChange={(e) => handleFieldChange('lastName', e.target.value)}
                className={USER_MODAL_INPUT_CLASS}
              />
            </div>

            {/* Phone extension - read-only */}
            {formData.phone && (
              <div>
                <label
                  htmlFor="edit-user-phone"
                  className="mb-1 block text-xs font-medium text-gray-3.0"
                >
                  Extension SIP (solo lectura)
                </label>
                <input
                  id="edit-user-phone"
                  type="text"
                  value={formData.phone}
                  disabled
                  className={classNames(
                    'w-full rounded-md border px-3 py-2 text-sm',
                    'border-white-2.0 bg-gray-0.0 text-gray-2.0 cursor-not-allowed'
                  )}
                />
                <p className="mt-1 text-[10px] text-gray-2.0">
                  La extension no se puede editar porque requiere redesplegar FreeSWITCH.
                </p>
              </div>
            )}

            {/* Is Admin toggle */}
            <div className="flex items-center gap-3 rounded-md border border-white-2.0 bg-white-0.0 px-3 py-3">
              <label htmlFor="edit-user-is-admin" className="flex-1 cursor-pointer">
                <p className="text-sm font-medium text-black-1.0">Rol de Administrador</p>
                <p className="text-xs text-gray-2.0">
                  Si se activa, el usuario tendra permisos de admin en la cuenta
                </p>
              </label>
              <button
                id="edit-user-is-admin"
                type="button"
                role="switch"
                aria-checked={formData.is_admin}
                onClick={() => handleFieldChange('is_admin', !formData.is_admin)}
                className={classNames(
                  'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-2.0 focus:ring-offset-2',
                  formData.is_admin ? 'bg-primary-2.0' : 'bg-gray-1.0'
                )}
              >
                <span
                  className={classNames(
                    'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                    formData.is_admin ? 'translate-x-5' : 'translate-x-0'
                  )}
                />
              </button>
            </div>

            {/* In Call Center toggle - only for shared accounts */}
            {isSharedAccount && (
              <div className="flex items-center gap-3 rounded-md border border-white-2.0 bg-white-0.0 px-3 py-3">
                <label htmlFor="edit-user-in-call-center" className="flex-1 cursor-pointer">
                  <p className="text-sm font-medium text-black-1.0">Recibir llamadas</p>
                  <p className="text-xs text-gray-2.0">
                    Si se activa, el usuario puede recibir llamadas entrantes en las colas del call
                    center
                  </p>
                </label>
                <button
                  id="edit-user-in-call-center"
                  type="button"
                  role="switch"
                  aria-checked={formData.in_call_center}
                  onClick={() => handleFieldChange('in_call_center', !formData.in_call_center)}
                  className={classNames(
                    'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-2.0 focus:ring-offset-2',
                    formData.in_call_center ? 'bg-primary-2.0' : 'bg-gray-1.0'
                  )}
                >
                  <span
                    className={classNames(
                      'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                      formData.in_call_center ? 'translate-x-5' : 'translate-x-0'
                    )}
                  />
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={() => modalRef.current?.hide()}
            className="btn btn-ghost flex items-center"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={!isFormValid || loading}
            onClick={() => void handleUpdate()}
            className="btn btn-secondary flex items-center"
          >
            {loading ? (
              <>
                <span className="animate-spin border-t-transparent w-4 h-4 mr-2 border-2 border-white rounded-full" />
                Guardando...
              </>
            ) : (
              <>
                Guardar Cambios
                <Icon name={OrbitIcons.Check} size={14} className="ml-2" />
              </>
            )}
          </button>
        </div>
      </Modal>
    );
  }
);
